import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, auth, type AccountSummary } from '../api';
import { run, useAsync, useToast } from '../hooks';

const fmt = (iso?: string) => (iso ? new Date(iso).toLocaleDateString() : '—');

/** Plan, trial and billing for the signed-in account (planning.md FR-B-01..FR-B-04, D-21, D-28). */
export function BillingPage() {
  const [params, setParams] = useSearchParams();
  const accountId = params.get('accountId') ?? undefined; // operators pick an account; users see their own
  const operatorNeedsPick = !!auth.token && !accountId;
  const { data, error, loading, reload } = useAsync(() => (operatorNeedsPick ? Promise.resolve(null) : api.account(accountId)), [accountId, operatorNeedsPick]);
  const { toast, show } = useToast();
  const [busy, setBusy] = useState(false);
  const [synced, setSynced] = useState<string | null>(null);

  // Back from checkout: confirm the outcome with the API (the webhook may arrive later).
  useEffect(() => {
    const checkoutId = params.get('checkout');
    if (!checkoutId || synced === checkoutId) return;
    setSynced(checkoutId);
    void (async () => {
      const r = await run(api.syncCheckout(checkoutId, { accountId, mock: params.get('mock') === '1' }), show);
      if (r) {
        show(r.checkout.status === 'COMPLETED' ? 'Payment received. Your plan is active.' : `Checkout ${r.checkout.status.toLowerCase()}.`, r.checkout.status !== 'COMPLETED');
        const next = new URLSearchParams(params);
        next.delete('checkout');
        next.delete('mock');
        setParams(next, { replace: true });
        await reload();
      }
    })();
  }, [params, accountId, synced, show, setParams, reload]);

  const upgrade = async () => {
    setBusy(true);
    const r = await run(api.checkout(accountId), show);
    setBusy(false);
    if (r) window.location.assign(r.url);
  };
  const cancel = async () => {
    if (!confirm('Cancel the subscription? The Springboard stops rendering for visitors at the end of the paid period.')) return;
    setBusy(true);
    const r = await run(api.cancelSubscription(accountId), show, 'Subscription cancelled');
    setBusy(false);
    if (r) await reload();
  };

  return (
    <div>
      <div className="topbar">
        <div>
          <h1>Plan &amp; billing</h1>
          <p className="muted small">{operatorNeedsPick ? 'Operator view: choose a customer account.' : 'Trial, limits and payment for this account.'}</p>
        </div>
        <Link className="btn sm" to="/sites">Back to sites</Link>
      </div>
      {params.get('cancelled') && <p className="badge warn" style={{ marginBottom: 12 }}>Checkout was cancelled. Nothing was charged.</p>}
      {operatorNeedsPick && <AccountPicker />}
      {error && <p className="badge bad">{error}</p>}
      {loading && !data && <p className="muted">Loading…</p>}
      {data && <Summary data={data} busy={busy} onUpgrade={upgrade} onCancel={cancel} />}
      {toast && <div className={`toast${toast.err ? ' err' : ''}`}>{toast.text}</div>}
    </div>
  );
}

/** Operators manage many customers: list the accounts (grouped from the sites they own) and open one. */
function AccountPicker() {
  const { data, error, loading } = useAsync(() => api.sites(), []);
  if (loading && !data) return <p className="muted">Loading accounts…</p>;
  if (error) return <p className="badge bad">{error}</p>;
  const groups = new Map<string, string[]>();
  for (const s of data?.sites ?? []) groups.set(s.accountId, [...(groups.get(s.accountId) ?? []), s.primaryUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')]);
  return (
    <div className="card" style={{ padding: 0 }}>
      <table>
        <thead><tr><th>Customer account</th><th>Sites</th><th></th></tr></thead>
        <tbody>
          {[...groups.entries()].map(([id, sites]) => (
            <tr key={id}>
              <td className="mono">{id}</td>
              <td>{sites.join(', ')}</td>
              <td style={{ textAlign: 'right' }}><Link className="btn sm" to={`/billing?accountId=${encodeURIComponent(id)}`}>Open billing</Link></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Summary({ data, busy, onUpgrade, onCancel }: { data: AccountSummary; busy: boolean; onUpgrade: () => void; onCancel: () => void }) {
  const t = data.trial;
  const paid = data.plan === 'paid';
  const headline =
    t.phase === 'paid' ? 'Paid plan' : t.phase === 'cancelled' ? 'Subscription cancelled' : t.phase === 'not_started' ? 'Free trial (starts at the first widget load)' : t.phase === 'running' ? `Free trial · ${t.daysLeft} days left` : t.phase === 'grace' ? 'Trial ended · grace period' : 'Trial ended';
  const cls = paid ? 'ok' : t.phase === 'expired' || t.phase === 'cancelled' ? 'bad' : t.phase === 'grace' ? 'warn' : 'accent';
  return (
    <div className="grid" style={{ gap: 18 }}>
      <div className="card">
        <div className="row spread">
          <div className="row">
            <h2>{data.account.email}</h2>
            <span className={`badge ${cls}`}>{headline}</span>
            {data.account.status === 'past_due' && <span className="badge warn">payment failed, retrying</span>}
          </div>
          <div className="row">
            {!paid && (
              <button className="btn primary" disabled={busy} onClick={onUpgrade}>
                Upgrade · US${data.priceUsd}/month
              </button>
            )}
            {paid && data.billing?.subscriptionId && (
              <button className="btn sm" disabled={busy} onClick={onCancel}>
                Cancel subscription
              </button>
            )}
          </div>
        </div>
        <div className="grid cols-4" style={{ marginTop: 16 }}>
          <div className="stat"><div className="l">Plan</div><div className="v" style={{ fontSize: 18 }}>{data.planLabel}</div></div>
          <div className="stat"><div className="l">Sites</div><div className="v">{data.usage.sites}{data.limits.sites ? ` / ${data.limits.sites}` : ''}</div></div>
          <div className="stat"><div className="l">Scans used</div><div className="v">{data.usage.scansUsed}{data.limits.scansTotal ? ` / ${data.limits.scansTotal}` : ''}</div></div>
          <div className="stat"><div className="l">Pages per scan</div><div className="v">{data.limits.maxPages.toLocaleString()}</div></div>
        </div>
        <p className="small muted" style={{ marginTop: 10 }}>
          {t.phase === 'not_started' && 'The 14-day trial starts when the Springboard first loads on your site. Install the snippet to begin.'}
          {t.phase === 'running' && `Trial started ${fmt(t.trialStartsAt)} and ends ${fmt(t.trialEndsAt)}. After a 3-day grace period the Springboard stops showing to visitors until you upgrade.`}
          {t.phase === 'grace' && `The trial ended ${fmt(t.trialEndsAt)}. Visitors still see the Springboard until ${fmt(t.graceEndsAt)}; upgrade to keep it on.`}
          {t.phase === 'expired' && 'The trial and grace period are over: the Springboard is hidden from visitors. Your configuration and results are kept; upgrade to switch it back on.'}
          {t.phase === 'paid' && `Subscription ${data.billing?.subscriptionStatus?.toLowerCase() ?? 'active'}${data.billing?.currentPeriodEndsAt ? `, renews ${fmt(data.billing.currentPeriodEndsAt)}` : ''}. Card, invoices and tax are handled by Airwallex.`}
          {t.phase === 'cancelled' && `Cancelled ${fmt(data.billing?.cancelledAt)}. The Springboard is hidden from visitors. Upgrade again at any time.`}
        </p>
      </div>

      <div className="grid cols-2">
        <div className="card">
          <h3>Free trial</h3>
          <ul className="small muted" style={{ marginTop: 8, paddingLeft: 18, listStyle: 'disc' }}>
            <li>One website, scans of the top 200 pages, three scans in total</li>
            <li>Full Springboard, two questions, search and the 14-day A/B test</li>
            <li>Card details only when you upgrade</li>
          </ul>
        </div>
        <div className="card">
          <h3>Paid · US${data.priceUsd} per site per month</h3>
          <ul className="small muted" style={{ marginTop: 8, paddingLeft: 18, listStyle: 'disc' }}>
            <li>Full-site scans (up to 2,000 pages) and daily re-scans</li>
            <li>Keep the Springboard on after the trial, or keep testing any two published versions</li>
            <li>USD billing through Airwallex with automatic tax; invoices by email</li>
          </ul>
        </div>
      </div>
      {data.provider === 'mock' && <p className="small muted">Billing provider: local mock. Upgrading here simulates a completed checkout; no payment is taken.</p>}
    </div>
  );
}
